import { type JSX, Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { LoadingSpinner } from '~/components/LoadingSpinner';

import { DocBreadcrumb } from './components/DocBreadcrumb';
import { getContentComponent } from './content';

/**
 * DocPage - Renders documentation content based on URL slug.
 *
 * Routes:
 * - /docs/:sectionSlug -> Renders section content
 * - /docs/:sectionSlug/:subSlug -> Renders subsection content (future)
 *
 * Handles:
 * - Dynamic content loading via content registry
 * - 404 for unknown slugs
 *
 * Note: Scroll reset is handled by DocsLayout (owns the scroll container).
 */
export function DocPage(): JSX.Element {
  const { sectionSlug } = useParams<{ sectionSlug: string; subSlug?: string }>();

  // Get content component for this slug
  const ContentComponent = sectionSlug ? getContentComponent(sectionSlug) : null;

  // 404 for unknown slugs
  if (!ContentComponent) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-foreground mb-4">Page Not Found</h1>
        <p className="text-muted mb-6">
          The documentation page you&apos;re looking for doesn&apos;t exist.
        </p>
        <a href="/docs" className="text-primary hover:underline">
          Return to Documentation Home
        </a>
      </div>
    );
  }

  return (
    <>
      <DocBreadcrumb />
      <Suspense
        fallback={
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        }
      >
        <ContentComponent />
      </Suspense>
    </>
  );
}
